import * as THREE from "three";
import Experience from "./Experience";
import CameraPath from "./CameraPath";

// Slope multiplier fed into softLimit — hardcoded constants in the
// reference, NOT derived from any preset field (separate from
// tilt.maxYaw/maxPitch below, which are the soft-limit CAP).
const MAX_YAW = THREE.MathUtils.degToRad(3.5);
const MAX_PITCH = THREE.MathUtils.degToRad(2.2);

// v * slope/cap run through tanh, scaled back up by cap — bounds the tilt
// angle smoothly instead of hard-clamping it.
function softLimit(v, slope, cap) {
  return cap > 0 ? cap * Math.tanh((v * slope) / cap) : 0;
}

function clamp01(v) {
  return THREE.MathUtils.clamp(v, 0, 1);
}

export default class Camera {
  constructor() {
    this.experience = new Experience();
    this.sizes = this.experience.sizes;
    this.scene = this.experience.scene;
    this.canvas = this.experience.canvas;
    this.sceneData = this.experience.sceneData;

    this.cameraPath = new CameraPath();

    this.setInstance();
    this.setTilt();
    this.setPointer();
  }

  setInstance() {
    // near/far widened for the reference's AE-comp-pixel-scale coordinates
    // (camera positions run in the hundreds to low-thousands of units) —
    // the old 0.1/100 range was tuned for a hand-built ~10-unit scene and
    // would clip this scene's own camera path.
    this.instance = new THREE.PerspectiveCamera(
      35,
      this.sizes.width / this.sizes.height,
      1,
      10000,
    );
    this.scene.add(this.instance);

    this.applyViewZoom();
  }

  // Permanent overscan crop (reference: applyViewZoom/groupMargin, preset
  // sceneZoom: 35 -> 0.35): the camera renders a wider frustum than is
  // actually displayed, cropped to a centred sub-rect via setViewOffset.
  // This is what gives the tilt orbit headroom before the plate's
  // frustum-fit edge becomes visible — without it, a plate sized to
  // exactly fill the untilted frustum runs out of coverage the moment the
  // camera orbits by any real angle.
  //
  // The reference measures that crop rectangle in fixed AE comp pixels
  // (1920x1080, a ~16:9 shape) because its player is always that aspect.
  // Our canvas is responsive — at a window aspect far from 16:9 (very
  // short/wide, or narrow/tall), a crop rectangle measured in a
  // mismatched reference frame zooms the two axes by different amounts
  // (setViewOffset scales relative to the full frame's own shape), which
  // reads as the whole scene shrinking unevenly. The reference frame here
  // is built to match the camera's OWN live aspect instead, so the
  // resulting zoom is always the same uniform factor on both axes.
  applyViewZoom() {
    const sceneZoom = Number(this.sceneData.preset.sceneZoom) / 100;
    const fullW = 1000;
    const fullH = fullW / this.instance.aspect;
    const m = 1 / (1 + sceneZoom);
    const w = fullW * m;
    const h = fullH * m;

    this.instance.setViewOffset(fullW, fullH, (fullW - w) / 2, (fullH - h) / 2, w, h);
    this.instance.updateProjectionMatrix();
  }

  setTilt() {
    const preset = this.sceneData.preset;

    this.tilt = {
      on: preset.tiltOn,
      amount: Number(preset.tiltAmt) / 100,
      soft: preset.tiltSoft,
      maxYaw: THREE.MathUtils.degToRad(Number(preset.tiltMaxYaw)),
      maxPitch: THREE.MathUtils.degToRad(Number(preset.tiltMaxPitch)),
    };
    this.tiltGate = preset.tiltGate;
    this.tiltGateClip = preset.tiltGateClip; // e.g. "sky" — tilt live once past this clip's end
    this.tiltGain = 0; // eases 0->1 once the gate opens

    this.circular = {
      on: preset.circOn,
      speed: Number(preset.circSpd) / 100,
      radius: Number(preset.circR) / 100,
    };
    this.circPhase = 0;
    this.circWeight = 0;

    // Normalized -1..1 tilt curve, read by the turntable to match its own
    // rotation to the camera's tilt.
    this.tiltNorm = { x: 0, y: 0 };
  }

  setPointer() {
    this.pointer = { rx: 0, ry: 0, tx: 0, ty: 0, x: 0, y: 0, inside: false };
    this.mouseIdle = 0;

    window.addEventListener("pointermove", (event) => {
      this.pointer.rx = (event.clientX / this.sizes.width) * 2 - 1;
      this.pointer.ry = (event.clientY / this.sizes.height) * 2 - 1;
      this.pointer.inside = true;
      this.mouseIdle = 0;
    });

    document.addEventListener("mouseout", (event) => {
      if (!event.relatedTarget) {
        this.pointer.inside = false;
      }
    });
  }

  // Whether the playhead has passed the gate clip's end — tilt only
  // becomes live past that point (matches the reference's tiltReady()).
  isTiltReady(playheadFrame) {
    if (!this.tiltGate) return true;
    const gateClip = this.sceneData.clips.find((c) => c.id === this.tiltGateClip);
    return playheadFrame >= gateClip.to - 0.5;
  }

  // Pointer easing + the circular idle sway. Continuously re-seeds circPhase
  // from the live pointer angle while the pointer leads (circWeight small),
  // so handoff between pointer-driven and idle-sway motion is seamless in
  // both directions — see drivePointer in the reference.
  drivePointer(dt, k) {
    this.mouseIdle += dt;

    let tx = this.pointer.rx;
    let ty = this.pointer.ry;
    let want = 0;

    if (this.circular.on) {
      want = this.pointer.inside ? clamp01((this.mouseIdle - 0.35) / 0.7) : 1;
      this.circPhase += dt * this.circular.speed * Math.PI * 2;
    }

    this.circWeight += (want - this.circWeight) * k;

    if (this.circWeight < 0.02 && this.pointer.inside) {
      this.circPhase = Math.atan2(ty, tx);
    }

    if (this.circWeight > 0) {
      const ax = Math.cos(this.circPhase) * this.circular.radius;
      const ay = Math.sin(this.circPhase) * this.circular.radius;
      tx += (ax - tx) * this.circWeight;
      ty += (ay - ty) * this.circWeight;
    }

    this.pointer.tx = tx;
    this.pointer.ty = ty;
  }

  // Rigid orbit delta applied on top of the baked camera pose — yaw/pitch
  // rotate both position (around the pivot) and orientation together, so
  // pointer-at-centre reproduces the untilted bake exactly.
  applyTilt(playheadFrame) {
    const pivotDist = this.cameraPath.getPivotDistance(playheadFrame);

    const dir = new THREE.Vector3();
    this.instance.getWorldDirection(dir);
    const pivot = this.instance.position.clone().addScaledVector(dir, pivotDist);

    const amt = this.tilt.amount * this.tiltGain;
    const yaw = this.tilt.soft
      ? -softLimit(this.pointer.x, MAX_YAW * amt, this.tilt.maxYaw * this.tiltGain)
      : -this.pointer.x * MAX_YAW * amt;
    const pitch = this.tilt.soft
      ? -softLimit(this.pointer.y, MAX_PITCH * amt, this.tilt.maxPitch * this.tiltGain)
      : -this.pointer.y * MAX_PITCH * amt;

    this.tiltNorm.x = this.tilt.maxYaw > 0 ? -yaw / this.tilt.maxYaw : 0;
    this.tiltNorm.y = this.tilt.maxPitch > 0 ? -pitch / this.tilt.maxPitch : 0;

    const right = new THREE.Vector3().crossVectors(dir, this.instance.up).normalize();

    const qYaw = new THREE.Quaternion().setFromAxisAngle(this.instance.up, yaw);
    const qPitch = new THREE.Quaternion().setFromAxisAngle(right, pitch);
    const q = qYaw.multiply(qPitch);

    const offset = this.instance.position.clone().sub(pivot).applyQuaternion(q);
    this.instance.position.copy(pivot).add(offset);
    this.instance.quaternion.premultiply(q);

    this.pivotDist = pivotDist;
    // Exposed so the plate (SequenceBG.updatePlate) can orbit its own
    // camera by the same yaw/pitch, keeping plate-vs-scene parallax an
    // exact plateDepth ratio instead of billboarding to this camera.
    this.tiltYaw = yaw;
    this.tiltPitch = pitch;
  }

  resize() {
    this.instance.aspect = this.sizes.width / this.sizes.height;
    // Re-applied, not just updateProjectionMatrix(): the view-offset crop
    // rectangle itself was measured against the OLD aspect (see
    // applyViewZoom) and goes stale on an aspect change, not just the
    // projection matrix.
    this.applyViewZoom();
  }

  update(dt, playheadFrame) {
    const k = Math.min(1, dt * 6);

    this.cameraPath.applyToCamera(this.instance, playheadFrame);

    const gateReady = this.tilt.on && this.isTiltReady(playheadFrame);
    this.tiltGain += ((gateReady ? 1 : 0) - this.tiltGain) * k;

    this.drivePointer(dt, k);
    this.pointer.x += (this.pointer.tx - this.pointer.x) * k;
    this.pointer.y += (this.pointer.ty - this.pointer.y) * k;

    this.applyTilt(playheadFrame);
  }
}
